// src/Shop.js
import React, { useState } from 'react';
import { db } from './firebase';
import { doc, collection, addDoc, setDoc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { COLORS } from './theme';
import { formatCurrency, getUserRankIds, SHOP_ITEMS as DEFAULT_SHOP_ITEMS } from './utils';
import './App.css';

const Shop = ({ user, appConfig, onBack }) => {
  const [loadingId, setLoadingId] = useState(null);

  // ★ 管理画面の設定値を取得
  const pointName = appConfig?.pointNameLabel || 'RB Coin'; // 通貨名 (例: RBコイン)
  const pointUnit = appConfig?.pointUnitLabel || 'RB';      // 単位 (例: RB)
  
  // 商品リストの取得
  const shopItems = (appConfig?.shopItems && appConfig.shopItems.length > 0) 
      ? appConfig.shopItems 
      : DEFAULT_SHOP_ITEMS;

  // ★ 修正: 以前は user.rankIds の参照を直接 push しており、
  //   描画のたびに user オブジェクトを破壊していた（配列が無限に伸びる）。
  //   判定は utils に一本化する。有効期限切れも自動で除外される。
  const hasRank = (rankId) => {
    if (!user || !rankId) return false;
    return getUserRankIds(user).includes(rankId);
  };

  const handlePurchase = async (item) => {
    if (!user) return;
    setLoadingId(item.id);

    try {
      if (item.currency === 'POINT' || item.paymentMethod === 'point') {
        await handlePointPurchase(item);
      } else {
        await handleJpyPurchase(item);
      }
    } catch (e) {
      console.error(e);
      toast.error('注文処理中にエラーが発生しました');
    } finally {
      setLoadingId(null);
    }
  };

  // 1. 日本円決済
  const handleJpyPurchase = async (item) => {
    if (item.type === 'rank' && item.targetId && hasRank(item.targetId)) {
      const { isConfirmed } = await Swal.fire({
        title: '購入済みです',
        text: 'すでにこの権限をお持ちのようですが、期間延長として購入しますか？',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: '延長購入する',
        cancelButtonText: 'やめる'
      });
      if (!isConfirmed) return;
    }

    // 1. 支払い方法の選択
    const { value: paymentMethod } = await Swal.fire({
      title: 'お支払い方法を選択',
      text: `${item.name} (¥${Number(item.price).toLocaleString()})`,
      input: 'radio',
      inputOptions: {
        'paypay': 'PayPay送金',
        'bank': '銀行振込'
      },
      inputValidator: (value) => {
        if (!value) return '支払い方法を選択してください';
      },
      showCancelButton: true,
      confirmButtonText: '次へ'
    });

    if (!paymentMethod) return;

    // ★ 第6段：PayPay ID は private から取得する（親からは削除されるため）
    let userPayPayId = '';
    try {
        const privSnap = await getDoc(doc(db, 'users', user.userId, 'private', 'profile'));
        if (privSnap.exists()) userPayPayId = privSnap.data().paypayId || '';
    } catch (e) {
        console.warn('PayPay ID の取得に失敗:', e?.message || e);
    }
    const savedPayPayId = userPayPayId;
    if (paymentMethod === 'paypay') {
        const { value: inputId, isDismissed } = await Swal.fire({
            title: 'PayPay IDの確認',
            html: `
                <p style="font-size:13px; text-align:left;">
                    入金確認のため、<b>あなたのアカウントID</b>を入力してください。<br>
                    <small style="color:#666">※送金メモ等が使えない場合の照合に使用します</small>
                </p>
            `,
            input: 'text',
            inputValue: userPayPayId,
            inputPlaceholder: '例: my_account_id',
            showCancelButton: true,
            confirmButtonText: '次へ',
            inputValidator: (value) => {
                if (!value) return 'IDを入力してください';
            }
        });
        if (isDismissed) return;
        userPayPayId = inputId;
    }

    // 2. 案内表示の構築
    let infoHtml = '';
    if (paymentMethod === 'bank') {
      infoHtml = `<div style="text-align:left; background:#f5f5f5; padding:10px; border-radius:8px; font-size:12px; white-space:pre-wrap;"><b>銀行振込先:</b><br>${appConfig?.bankInfo || '振込先情報が未設定です。'}</div>`;
    } else {
      infoHtml = `
        <div style="text-align:left; background:#f5f5f5; padding:10px; border-radius:8px; font-size:12px;">
            <div style="margin-bottom:5px;"><b>送金先 (管理者ID):</b><br><span style="font-size:16px; font-weight:bold; color:#e65100;">${appConfig?.adminPayPayId || '未設定'}</span></div>
            <div style="border-top:1px dashed #ccc; padding-top:5px;"><b>あなたのID:</b> ${userPayPayId}</div>
        </div>`;
    }

    // 3. 最終確認
    const result = await Swal.fire({
      title: '注文の確認',
      html: `
        <div style="margin-bottom:15px;">
          <div style="font-weight:bold; font-size:18px;">${item.name}</div>
          <div style="color:#e65100; font-size:20px; font-weight:bold;">¥${Number(item.price).toLocaleString()}</div>
        </div>
        ${infoHtml}
        <p style="font-size:12px; color:#666; margin-top:10px;">
          「注文する」を押した後、選択した方法でお支払いをお願いします。<br>
          管理者が入金を確認次第、付与されます。
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: '注文する',
      confirmButtonColor: COLORS.primary
    });

    if (result.isConfirmed) {
      // 注文作成（PayPayIDも渡す）
      await createOrder(item, paymentMethod, userPayPayId);
      
      // プロフィールにIDがない場合は更新しておく
      if (paymentMethod === 'paypay' && userPayPayId && savedPayPayId !== userPayPayId) {
          try {
             // ★ 第6段：親には書かず、private のみを更新する
             await setDoc(
                 doc(db, 'users', user.userId, 'private', 'profile'),
                 { paypayId: userPayPayId, updatedAt: new Date().toISOString() },
                 { merge: true }
             );
          } catch(e) { console.error("ID update error", e); }
      }

      Swal.fire('注文完了', '管理者に注文を送信しました。\n承認をお待ちください。', 'success');
    }
  };

  // 2. ポイント決済
  const handlePointPurchase = async (item) => {
    const currentPoints = user.points || 0;
    const price = Number(item.price);

    if (currentPoints < price) {
      await Swal.fire({
        title: '残高不足',
        text: `${pointUnit}が足りません。\n現在の所持数: ${formatCurrency(currentPoints)} ${pointUnit}`,
        icon: 'error',
        confirmButtonText: '閉じる',
        confirmButtonColor: '#d33'
      });
      return;
    }

    const result = await Swal.fire({
      title: '購入確認',
      html: `
        <div><b>${item.name}</b> を購入しますか？</div>
        <div style="margin: 10px 0;">価格: <b>${price} ${pointUnit}</b></div>
        <div style="font-size:12px; color:#666;">現在の所持数: ${formatCurrency(currentPoints)} ${pointUnit}</div>
        <p style="font-size:11px; color:#d32f2f; margin-top:10px;">※管理者が承認した時点でポイント消費・付与されます。</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '注文する',
      confirmButtonColor: '#e65100'
    });

    if (result.isConfirmed) {
      await createOrder(item, 'point', null);
      Swal.fire('注文完了', '購入リクエストを送信しました。\n管理者の承認をお待ちください。', 'success');
    }
  };

  // ★ 修正: paypayId引数を追加
  const createOrder = async (item, method, paypayId = null) => {
      const orderData = {
        userId: user.userId,
        userName: user.displayName,
        itemId: item.id,
        itemName: item.name,
        price: Number(item.price) || 0,
        currency: item.currency || (method === 'point' ? 'POINT' : 'JPY'),
        paymentMethod: method, 
        status: 'pending', 
        requestedAt: new Date().toISOString()
      };

      // PayPayIDがある場合は保存
      if (paypayId) {
          orderData.paypayId = paypayId;
      }

      await addDoc(collection(db, 'shop_orders'), orderData);
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', marginTop: '10px' }}>
        <button onClick={onBack} className="btn" style={{ background: 'none', border: 'none', fontSize: '20px', padding: '0 10px 0 0', cursor: 'pointer' }}>
          ←
        </button>
        <h2 style={{ margin: 0, fontSize: '20px' }}>🛒 ショップ</h2>
      </div>

      {/* ★ 修正: 通貨名・単位・画像を表示 */}
      <div className="card" style={{ background: 'linear-gradient(135deg, #263238, #37474F)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px' }}>
        <div>
          <div style={{ fontSize: '10px', opacity: 0.8 }}>所持 {pointName}</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {formatCurrency(user.points || 0)} <span style={{ fontSize: '14px' }}>{pointUnit}</span>
          </div>
        </div>
        <img src={`${process.env.PUBLIC_URL}/rbcoin.png`} alt="coin" style={{width:'50px', height:'50px', objectFit:'contain'}} />
      </div>

      <div style={{ display: 'grid', gap: '15px' }}>
        {shopItems.map((item) => {
          const isPointItem = item.currency === 'POINT' || item.paymentMethod === 'point';
          const alreadyOwned = item.type === 'rank' && item.targetId && hasRank(item.targetId);

          return (
            <div key={item.id} className="card" style={{ display: 'flex', gap: '15px', padding: '15px', borderLeft: isPointItem ? '5px solid #e65100' : `5px solid ${COLORS.secondary}` }}>
              
              {item.imageUrl ? (
                  <div style={{ width: '60px', height: '60px', flexShrink: 0 }}>
                    <img 
                        src={item.imageUrl} 
                        alt={item.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #eee' }} 
                    />
                  </div>
              ) : (
                  <div style={{ 
                      width: '60px', height: '60px', 
                      background: isPointItem ? '#fff3e0' : '#fff8e1', 
                      borderRadius: '12px', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center', 
                      fontSize: '30px', flexShrink: 0 
                  }}>
                    {item.icon || (item.type==='rank'?'👑':'🎁')}
                  </div>
              )}

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#333' }}>
                    {item.name}
                    {alreadyOwned && <span style={{ fontSize: '10px', background: '#4caf50', color: 'white', padding: '2px 6px', borderRadius: '4px', marginLeft: '5px', verticalAlign: 'middle' }}>保有中</span>}
                  </h3>
                </div>
                
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', lineHeight: '1.4' }}>
                  {item.description}
                  {item.validityLabel && <div style={{color: COLORS.primary, marginTop:'3px', fontWeight:'bold'}}>{item.validityLabel}</div>}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '16px', color: isPointItem ? '#e65100' : '#333' }}>
                    {isPointItem ? `${Number(item.price)} ${pointUnit}` : `¥${Number(item.price).toLocaleString()}`}
                  </div>

                  <button 
                    onClick={() => handlePurchase(item)}
                    disabled={loadingId === item.id}
                    className="btn"
                    style={{
                      background: isPointItem ? '#e65100' : COLORS.secondary,
                      color: isPointItem ? 'white' : '#333',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: loadingId === item.id ? 0.7 : 1
                    }}
                  >
                    {loadingId === item.id ? '処理中...' : '注文'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {shopItems.length === 0 && <div style={{textAlign:'center', color:'#999', padding:'20px'}}>販売中の商品はありません</div>}
      </div>

      <div style={{ marginTop: '20px', padding: '15px', background: '#e3f2fd', borderRadius: '8px', fontSize: '11px', color: '#0d47a1', lineHeight: '1.6' }}>
        <strong>ℹ️ 購入について</strong><br />
        ・すべての商品は、注文後に管理者が承認することで付与されます。<br />
        ・購入状況はマイページの履歴等で確認できます。
      </div>
    </div>
  );
};

export default Shop;